import { Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { translate } from 'react-switch-lang';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { useMutatePractice } from 'src/hooks/useMutatePractice';
import { usePractices } from 'src/hooks/usePractices';
import SweetAlert from 'sweetalert2';
import Breadcrumb from '../../breadcrumb/index';
import { Practice } from '../../model/types';
import styles from '../../styles/style-setting.module.scss';
import CreatePractice from './CreatePractice';
import DetailPractice from './DetailPractice';
import EditPractice from './EditPractice';
import PracticeColumns from './PracticeColumns';

const NUMBER_OF_TECHNIQUES_PER_PAGE = 15;

const PracticePage = (props) => {
  const { practices, setPractices } = usePractices();
  const { createPractice, updatePracticeById } = useMutatePractice();

  const [search, setSearch] = useState('');
  const [itemOffset, setItemOffset] = useState(0);
  const [currentPractice, setCurrentPractice] = useState<Practice | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const filtered = practices.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase())
  );

  const endOffset = itemOffset + NUMBER_OF_TECHNIQUES_PER_PAGE;
  const pageCount = Math.ceil(filtered.length / NUMBER_OF_TECHNIQUES_PER_PAGE);
  const currentItems = filtered.slice(itemOffset, endOffset);

  const handleSearch = (e) => setSearch(e.target.value);
  const handlePageClick = (e) => {
    const newOffset = (e.selected * NUMBER_OF_TECHNIQUES_PER_PAGE) % filtered.length;
    setItemOffset(newOffset);
  };

  const onDetail = (practice: Practice) => {
    setCurrentPractice(practice);
    setIsDetailOpen(true);
  };

  const onEdit = (practice: Practice) => {
    setCurrentPractice(practice);
    setIsEditOpen(true);
  };

  const onDelete = async (practice: Practice) => {
    const result = await SweetAlert.fire({
      title: props.t('practice.you_delete'),
      text: props.t('practice.not_recover'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: props.t('practice.yes_delete'),
      cancelButtonText: props.t('practice.cancel'),
    });

    if (result.isConfirmed) {
      const updated = { ...practice, active: false };
      const response = await updatePracticeById(practice.id, updated);

      if (response?.status === 200) {
        setPractices((prev) => prev.filter((t) => t.id !== practice.id));
        SweetAlert.fire(props.t('practice.deleted'), props.t('practice.practice_deleted'), 'success');
      } else {
        SweetAlert.fire(props.t('practice.error'), props.t('practice.err'), 'error');
      }
    }
  };

  useEffect(() => {
    if (!isEditOpen) setCurrentPractice(null);
  }, [isEditOpen]);

  return (
    <>
      <Breadcrumb parent={props.t('practice.practice')} url={'settings/practice'} />

      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <div className="float-right">
                  <CreatePractice practices={practices} setPractices={setPractices} createPractice={createPractice} />
                </div>
              </CardHeader>

              <CardBody>
                <input
                  onChange={handleSearch}
                  className="form-control col-md-6 mb-2"
                  placeholder={props.t('practice.search_practice')}
                />

                {currentItems.length === 0 ? (
                  <Alert variant="outlined" severity="info">
                    {props.t('practice.no_practice')}
                  </Alert>
                ) : (
                  <PracticeColumns data={currentItems} onEdit={onEdit} onDelete={onDelete} onDetail={onDetail} />
                )}

                <br />
                <div className="float-right">
                  {practices.length > NUMBER_OF_TECHNIQUES_PER_PAGE && (
                    <ReactPaginate
                      previousLabel="<"
                      nextLabel=">"
                      pageCount={pageCount}
                      onPageChange={handlePageClick}
                      containerClassName={styles['paginationButton']}
                      previousLinkClassName={styles['previousBtn']}
                      nextLinkClassName={styles['nextBtn']}
                      disabledClassName={styles['paginationDisabled']}
                      activeClassName={styles['paginationActive']}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <DetailPractice isOpen={isDetailOpen} setIsOpen={setIsDetailOpen} practice={currentPractice} />

      {currentPractice && (
        <EditPractice
          isOpen={isEditOpen}
          setIsOpen={setIsEditOpen}
          practice={currentPractice}
          setPractices={setPractices}
          updatePracticeById={updatePracticeById}
        />
      )}
    </>
  );
};

export default translate(PracticePage);
